import type { ServerMessage, ClientMessage } from "@/types";
import { useAuthStore } from "@/stores/authStore";

type MessageHandler = (message: ServerMessage) => void;
type StatusHandler = (connected: boolean) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private tokenGetter: () => Promise<string>;
  private handlers = new Set<MessageHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  private intentionalClose = false;

  constructor(url: string, tokenGetter: () => Promise<string>) {
    this.url = url;
    this.tokenGetter = tokenGetter;
  }

  async connect(): Promise<void> {
    this.intentionalClose = false;

    // Get fresh token on every connect/reconnect
    const token = await this.tokenGetter();

    return new Promise((resolve, reject) => {
      const wsUrl = token
        ? `${this.url}?token=${encodeURIComponent(token)}`
        : this.url;

      try {
        this.ws = new WebSocket(wsUrl);
      } catch {
        reject(new Error("WebSocket construction failed"));
        return;
      }

      this.ws.onopen = () => {
        console.log("[WS] Connected");
        this.reconnectAttempts = 0;
        this.startKeepalive();
        this.notifyStatus(true);
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string) as ServerMessage;
          this.handlers.forEach((handler) => handler(message));
        } catch {
          console.error("[WS] Failed to parse message:", event.data);
        }
      };

      this.ws.onclose = async (event) => {
        this.stopKeepalive();
        console.warn(
          `[WS] Closed — code: ${event.code}, reason: "${event.reason || "none"}", clean: ${event.wasClean}`,
        );
        this.notifyStatus(false);

        if (this.intentionalClose) return;

        // On the very first connect failure, check if it's an auth issue.
        // checkAuth() never throws — it sets isAuthenticated to false on failure.
        // If auth is gone, fast-fail to login instead of retrying 10 times.
        if (this.reconnectAttempts === 0) {
          await useAuthStore.getState().checkAuth();
          if (!useAuthStore.getState().isAuthenticated) {
            console.warn("[WS] Auth invalid after connect error — logging out");
            await useAuthStore.getState().logout();
            return;
          }
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 15000);
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
          this.reconnectTimeout = setTimeout(() => {
            this.connect().catch(() => {
              // Silently handle — onclose will trigger another retry
            });
          }, delay);
        } else {
          console.error("[WS] Max reconnect attempts reached");
        }
      };

      this.ws.onerror = () => {
        // Only reject the initial connect promise, not reconnects
        if (this.reconnectAttempts === 0 && this.ws?.readyState !== WebSocket.OPEN) {
          reject(new Error("WebSocket connection failed"));
        }
      };
    });
  }

  send(message: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn("[WS] Cannot send — not connected");
    }
  }

  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.stopKeepalive();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    this.ws?.close();
    this.ws = null;
    this.handlers.clear();
    this.statusHandlers.clear();
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    // Ping every 5 minutes to prevent API Gateway idle timeout (10 min default)
    this.keepaliveInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: "ping" }));
      }
    }, 5 * 60 * 1000);
  }

  private stopKeepalive(): void {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
  }

  private notifyStatus(connected: boolean): void {
    this.statusHandlers.forEach((h) => h(connected));
  }
}
